import { Match } from '../../types';
import { isMundialGroupMatch } from '../matchdayService';

export interface StagePrediction {
  user_id: string;
  match_id: string;
  predicted_score_a: number;
  predicted_score_b: number;
}

export interface StageParticipant {
  userId: string;
  name: string;
  points: number;
  predictedGoals: number;
  goalDiffFromReal: number;
}

export interface StageWinnerResult {
  id: string;
  title: string;
  prizePct: string;
  isComplete: boolean;
  leader: StageParticipant | null;
  winners: StageParticipant[];
  tied: boolean;
}

export interface StageRankingEntry {
  uuid: string;
  name: string;
  points: number;
}

export interface StageLeaderboard {
  id: string;
  title: string;
  prizePct?: string;
  isComplete: boolean;
  rankings: StageRankingEntry[];
  matchFilter: (m: Match) => boolean;
}

export const QUINIELA_STAGES: {
  id: string;
  title: string;
  prizePct: string;
  matchFilter: (m: Match) => boolean;
}[] = [
  {
    id: 'groups',
    title: 'Fase de Grupos',
    prizePct: '15%',
    matchFilter: (m) => isMundialGroupMatch(m),
  },
  {
    id: 'r32_r16',
    title: '16avos + 8avos',
    prizePct: '15%',
    matchFilter: (m) =>
      m.match_type === 'round_of_32' || m.match_type === 'round_of_16',
  },
  {
    id: 'qf_sf_final',
    title: 'Cuartos + Semis + 3er lugar + Final',
    prizePct: '20%',
    matchFilter: (m) =>
      m.match_type === 'quarter_final' ||
      m.match_type === 'semi_final' ||
      m.match_type === 'third_place' ||
      m.match_type === 'final',
  },
  {
    id: 'overall',
    title: 'Ganador Absoluto',
    prizePct: '50%',
    matchFilter: () => true,
  },
];

function calculateMatchPoints(
  pred: StagePrediction,
  match: Match
): number {
  if (match.score_a === null || match.score_b === null) return 0;

  if (
    pred.predicted_score_a === match.score_a &&
    pred.predicted_score_b === match.score_b
  ) {
    return 3;
  }

  if (
    match.score_a === match.score_b &&
    pred.predicted_score_a === pred.predicted_score_b
  ) {
    return 1;
  }

  const matchResult = Math.sign(match.score_a - match.score_b);
  const predictionResult = Math.sign(
    pred.predicted_score_a - pred.predicted_score_b
  );

  if (matchResult === predictionResult && matchResult !== 0) {
    return 1;
  }

  return 0;
}

function isStageComplete(stageMatches: Match[]): boolean {
  if (stageMatches.length === 0) return false;
  return stageMatches.every(
    (m) => m.score_a !== null && m.score_b !== null
  );
}

function pickWinners(
  participants: StageParticipant[],
  realGoals: number
): { winners: StageParticipant[]; tied: boolean } {
  if (participants.length === 0) {
    return { winners: [], tied: false };
  }

  const maxPoints = Math.max(...participants.map((p) => p.points));
  const top = participants.filter((p) => p.points === maxPoints);

  if (top.length === 1) {
    return { winners: top, tied: false };
  }

  const withDiff = top.map((p) => ({
    ...p,
    goalDiffFromReal: Math.abs(p.predictedGoals - realGoals),
  }));

  const minDiff = Math.min(...withDiff.map((p) => p.goalDiffFromReal));
  const winners = withDiff.filter((p) => p.goalDiffFromReal === minDiff);

  return { winners, tied: winners.length > 1 };
}

function buildStageParticipants(
  stage: (typeof QUINIELA_STAGES)[number],
  users: { uuid: string; name: string }[],
  predictions: StagePrediction[],
  allMatches: Match[],
  matchesById: Map<string, Match>
): {
  participants: StageParticipant[];
  stageMatches: Match[];
  complete: boolean;
  realGoals: number;
} {
  const stageMatches = allMatches.filter(stage.matchFilter);
  const playedMatches = stageMatches.filter(
    (m) => m.score_a !== null && m.score_b !== null
  );
  const complete = isStageComplete(stageMatches);
  const realGoals = playedMatches.reduce(
    (sum, m) => sum + (m.score_a ?? 0) + (m.score_b ?? 0),
    0
  );

  const participants: StageParticipant[] = users
    .filter((u) => u.name)
    .map((user) => {
      const userPreds = predictions.filter((p) => p.user_id === user.uuid);
      let points = 0;
      let predictedGoals = 0;

      userPreds.forEach((pred) => {
        const match = matchesById.get(pred.match_id);
        if (!match || !stage.matchFilter(match)) return;
        if (match.score_a === null || match.score_b === null) return;
        points += calculateMatchPoints(pred, match);
        predictedGoals += pred.predicted_score_a + pred.predicted_score_b;
      });

      return {
        userId: user.uuid,
        name: user.name,
        points,
        predictedGoals,
        goalDiffFromReal: Math.abs(predictedGoals - realGoals),
      };
    })
    .sort((a, b) => b.points - a.points);

  return { participants, stageMatches, complete, realGoals };
}

export function computeStageLeaderboards(
  users: { uuid: string; name: string }[],
  predictions: StagePrediction[],
  allMatches: Match[]
): StageLeaderboard[] {
  const matchesById = new Map(allMatches.map((m) => [m.id, m]));

  return QUINIELA_STAGES.filter((stage) => stage.id !== 'overall').map((stage) => {
    const { participants, complete } = buildStageParticipants(
      stage,
      users,
      predictions,
      allMatches,
      matchesById
    );

    return {
      id: stage.id,
      title: stage.title,
      prizePct: stage.prizePct,
      isComplete: complete,
      matchFilter: stage.matchFilter,
      rankings: participants.map((p) => ({
        uuid: p.userId,
        name: p.name,
        points: p.points,
      })),
    };
  });
}

export function computeStageWinners(
  users: { uuid: string; name: string }[],
  predictions: StagePrediction[],
  allMatches: Match[]
): StageWinnerResult[] {
  const matchesById = new Map(allMatches.map((m) => [m.id, m]));

  return QUINIELA_STAGES.map((stage) => {
    const { participants, complete, realGoals } = buildStageParticipants(
      stage,
      users,
      predictions,
      allMatches,
      matchesById
    );

    const { winners, tied } = complete
      ? pickWinners(participants, realGoals)
      : { winners: [], tied: false };

    const leader = participants[0] ?? null;

    return {
      id: stage.id,
      title: stage.title,
      prizePct: stage.prizePct,
      isComplete: complete,
      leader,
      winners: complete ? winners : leader ? [leader] : [],
      tied,
    };
  });
}

export function calculateOverallPoints(
  userId: string,
  predictions: StagePrediction[],
  matchesById: Record<string, { score_a: number | null; score_b: number | null }>
): number {
  let points = 0;
  predictions
    .filter((p) => p.user_id === userId)
    .forEach((pred) => {
      const match = matchesById[pred.match_id];
      if (match && match.score_a !== null && match.score_b !== null) {
        points += calculateMatchPoints(pred, match as Match);
      }
    });
  return points;
}
